import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  MainTableFilterSearchByModel,
  MainTableSortingType,
} from 'src/app/shared/models/main-table-filter.model';
import {
  MainTableFilterColumnData,
  MainTableFilterListOption,
} from '../main-table-column-filter/main-table-column-filter.component';

export interface MainTableMobileFiltersData {
  columns: string[];
  filterConfig: Record<string, MainTableFilterColumnData>;
  columnFilters: Record<string, MainTableFilterSearchByModel[]>;
  disabledColumnHeaders: string[];
  columnTitlesMap: Record<string, string>;
  orderByColumn: string;
  sortDirection: MainTableSortingType;
  sortingEnabled: boolean;
}

export interface MainTableMobileFiltersResult {
  clearAll?: boolean;
  column?: string;
  filters?: MainTableFilterSearchByModel[];
  sort?: Sort;
}

@Component({
  selector: 'app-main-table-mobile-filters',
  templateUrl: './main-table-mobile-filters.component.html',
  styleUrls: ['./main-table-mobile-filters.component.scss'],
  standalone: false,
})
export class MainTableMobileFiltersComponent implements OnDestroy {
  selectedColumn = '';
  pendingSort?: Sort;

  private readonly filterValueLabels: Record<string, Record<string, string>> = {};
  private readonly subscriptions = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: MainTableMobileFiltersData,
    private dialogRef: MatDialogRef<
      MainTableMobileFiltersComponent,
      MainTableMobileFiltersResult | undefined
    >
  ) {
    this.primeFilterValueLabels();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get availableColumns(): string[] {
    return this.data.columns.filter((column) => !!this.data.filterConfig[column]);
  }

  get hasActiveFilters(): boolean {
    return this.activeFilterCount > 0;
  }

  get activeFilterCount(): number {
    return Object.values(this.data.columnFilters).filter((filters) => filters.length > 0).length;
  }

  getFilterSummaryParts(column: string): string[] {
    return (this.data.columnFilters[column] ?? [])
      .flatMap((filter) => (Array.isArray(filter.value) ? filter.value : [filter.value]))
      .map((value) => this.filterValueLabels[column]?.[String(value)] ?? String(value));
  }

  getSortDirection(column: string): MainTableSortingType {
    if (this.pendingSort?.active === column) {
      return this.pendingSort.direction;
    }

    return this.data.orderByColumn.toLowerCase() === column.toLowerCase()
      ? this.data.sortDirection
      : '';
  }

  openColumn(column: string): void {
    this.selectedColumn = column;
  }

  back(): void {
    this.selectedColumn = '';
  }

  close(): void {
    this.dialogRef.close();
  }

  clearAll(): void {
    this.dialogRef.close({ clearAll: true });
  }

  applyColumn(column: string, filters: MainTableFilterSearchByModel[]): void {
    this.dialogRef.close({ column, filters, sort: this.pendingSort });
  }

  selectSort(sort: Sort): void {
    this.pendingSort = sort;
  }

  private primeFilterValueLabels(): void {
    Object.entries(this.data.filterConfig).forEach(([column, config]) => {
      this.captureOptionLabels(column, config.options);
      config.sections?.forEach((section) => {
        this.captureOptionLabels(column, section.options);
      });
    });
  }

  private captureOptionLabels(
    column: string,
    options$?: Observable<MainTableFilterListOption>
  ): void {
    if (!options$) {
      return;
    }

    this.subscriptions.add(
      options$.pipe(take(1)).subscribe((options) => {
        if (!this.filterValueLabels[column]) {
          this.filterValueLabels[column] = {};
        }

        options.forEach((option: string | { label: string; value: string | boolean }) => {
          const isString = typeof option === 'string';
          const value = isString ? option : option.value;
          const label = isString ? option : (option.label ?? String(value));
          this.filterValueLabels[column][String(value)] = label;
        });
      })
    );
  }
}
